import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './UpgradeModal.css';
import '../../pages/AIToolsPage.css';

/**
 * UpgradeModal - AI Study Tools paywall modal
 * 
 * Displays two paid plans (Locked-In and All-Gas) for users to upgrade.
 * Shows context-aware messaging based on which tool triggered the modal.
 * 
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Callback when modal closes
 * @param {string} currentTier - User's current tier (unused, user is always on Free when this appears)
 * @param {string} requiredTier - Minimum tier needed (unused in new design)
 * @param {string} featureName - Name of blocked feature (unused in new design)
 * @param {string} [featureDescription] - Optional detailed description (unused in new design)
 * @param {string} [triggerSource] - Source that triggered modal ('quiz'|'chat'|'audio') for context-aware subtitle
 * @param {boolean} [inline] - If true, renders inline instead of using portal (for page-specific overlays)
 * @param {boolean} [hideCloseButton] - If true, hides the X close button
 */
const UpgradeModal = ({
  isOpen,
  onClose,
  currentTier,
  requiredTier,
  featureName,
  featureDescription,
  triggerSource,
  inline = false,
  hideCloseButton = false
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  // Context-aware subtitles based on trigger source
  const subtitles = {
    quiz: 'Generate quizzes instantly from your materials.',
    chat: 'Ask questions and get explanations from your course.',
    audio: 'Turn your class into audiobooks and podcasts.',
    default: 'Choose how much help you want this semester.'
  };

  const subtitle = subtitles[triggerSource] || subtitles.default;

  // Feature-unlock mode: Basic user wanting audio (pro)
  const isFeatureUnlock = currentTier === 'basic' && requiredTier === 'pro';

  if (isFeatureUnlock) {
    const unlockContent = (
      <div className={`upgrade-modal-overlay ${inline ? 'upgrade-modal-overlay--inline' : ''}`} onClick={onClose}>
        <div className="upgrade-modal upgrade-modal--unlock" onClick={(e) => e.stopPropagation()}>
          {!hideCloseButton && (
            <button className="upgrade-modal-close" onClick={onClose} aria-label="Close modal">{'\u2715'}</button>
          )}

          {/* Scout header row */}
          <div className="unlock-scout-row">
            <img
              src="/scout_images/scout_thumbs_up.png"
              alt=""
              className="unlock-scout-img"
              draggable="false"
            />
            <div className="unlock-scout-bubble">
              I&rsquo;ll turn this into something you can listen to.
            </div>
          </div>

          <div className="upgrade-modal-header">
            <h2 className="upgrade-modal-title">Unlock audio learning</h2>
            <p className="upgrade-modal-subtitle">Turn your documents into podcasts and audiobooks.</p>
          </div>

          {/* Audio preview card (visual only) */}
          <div className="unlock-audio-preview">
            <div className="unlock-audio-play">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </div>
            <div className="unlock-audio-info">
              <span className="unlock-audio-title">Chapter Summary &ndash; 6:42</span>
              <span className="unlock-audio-sub">Generated from your document</span>
            </div>
            <div className="unlock-audio-wave" aria-hidden="true">
              <span /><span /><span /><span /><span />
            </div>
          </div>

          <ul className="unlock-features">
            <li className="unlock-feature-item">
              <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span>Turn your notes into audiobooks</span>
            </li>
            <li className="unlock-feature-item">
              <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span>Auto-generate study podcasts</span>
            </li>
            <li className="unlock-feature-item">
              <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span>Listen instead of rereading</span>
            </li>
            <li className="unlock-feature-item">
              <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span>Study while walking, driving, or lifting</span>
            </li>
          </ul>

          <p className="unlock-includes">Includes everything in Locked-In</p>

          <div className="unlock-cta-wrap">
            <button className="unlock-cta" onClick={() => handleUpgrade('pro')}>
              Go All-Gas for $11.99/month
            </button>
          </div>

          <p className="unlock-social-proof">Most students unlock this before their first exam.</p>
        </div>
      </div>
    );

    if (inline) return unlockContent;
    return createPortal(unlockContent, document.body);
  }

  // Plan definitions kept for feature-unlock path above
  const handleUpgrade = (planAlias) => {
    navigate('/subscription');
  };

  // Tool icons for the gate screen
  const toolIcons = {
    chat: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
    ),
    flash: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M12 4v16"/><path d="M2 12h20"/></svg>
    ),
    quiz: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
    ),
    audio: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
    ),
  };

  const tools = [
    { icon: 'chat', name: 'AI Chat', sub: 'Ask anything about your course', tier: 'basic', delay: 1 },
    { icon: 'flash', name: 'Flashcards', sub: 'Auto-generated from your notes', tier: 'basic', delay: 2 },
    { icon: 'quiz', name: 'Quizzes', sub: 'Test yourself before exams', tier: 'basic', delay: 3 },
    { icon: 'audio', name: 'Podcasts & Audiobooks', sub: 'Learn while walking or driving', tier: 'pro', delay: 4 },
  ];

  const modalContent = (
    <div className={`upgrade-modal-overlay ${inline ? 'upgrade-modal-overlay--inline' : ''}`} onClick={onClose}>
      <div className="tools-gate" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
        <div className="tools-gate__backdrop" />

        <div className="tools-gate__card">
          {/* Close button */}
          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className="tools-gate__close"
              aria-label="Close"
              style={{
                position: 'absolute', top: '12px', right: '12px', zIndex: 1,
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary, #999)', fontSize: '22px', lineHeight: 1,
                padding: '4px 8px', borderRadius: '4px',
              }}
            >
              ✕
            </button>
          )}

          {/* LEFT: Scout + emotional hook */}
          <div className="tools-gate__left">
            <div className="tools-gate__left-inner">
              <div className="tools-gate__bubble">
                <span>You don&rsquo;t need to reread everything.</span>
                <span>Upload your course and I&rsquo;ll break it down for you.</span>
                <span className="tools-gate__typing" aria-hidden="true">
                  <span className="tools-gate__dot" />
                  <span className="tools-gate__dot" />
                  <span className="tools-gate__dot" />
                </span>
              </div>
              <div className="tools-gate__scout-wrap">
                <img
                  src="/scout_images/scout_thinking.png"
                  alt=""
                  className="tools-gate__scout-img"
                  draggable="false"
                />
              </div>
              <p className="tools-gate__tagline">
                Most students don&rsquo;t study harder. They use tools.
              </p>
            </div>
          </div>

          {/* RIGHT: Locked tool previews + CTA */}
          <div className="tools-gate__right">
            <h2 className="tools-gate__title">Your study toolkit</h2>
            <p className="tools-gate__subtitle">Everything you need to stay ahead &mdash; ready when you are.</p>

            <div className="tools-gate__tools">
              {tools.map((t) => (
                <div
                  key={t.icon}
                  className={[
                    'tools-gate__tool',
                    `tools-gate__tool--${t.delay}`,
                    t.tier === 'pro' ? 'tools-gate__tool--pro' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className="tools-gate__tool-icon">
                    {toolIcons[t.icon]}
                  </div>
                  <div className="tools-gate__tool-text">
                    <span className="tools-gate__tool-name">{t.name}</span>
                    <span className="tools-gate__tool-sub">{t.sub}</span>
                  </div>
                  <span className={`tools-gate__tier-tag tools-gate__tier-tag--${t.tier}`}>
                    {t.tier === 'pro' ? 'Included in All-Gas' : 'Included with AI Tools'}
                  </span>
                  <span className="tools-gate__lock" aria-hidden="true">&#x1F512;</span>
                </div>
              ))}
            </div>

            <div className="tools-gate__ctas">
              <button
                className="tools-gate__cta-primary"
                onClick={() => handleUpgrade('basic')}
              >
                Start for $4.99/month
              </button>
              <p className="tools-gate__cta-includes">
                Includes AI chat, quizzes, and flashcards
              </p>

              <button
                className="tools-gate__cta-secondary"
                onClick={() => handleUpgrade('pro')}
              >
                Go All-Gas for $11.99/month
              </button>
              <p className="tools-gate__cta-secondary-sub">
                Includes audio learning tools
              </p>

              <p className="tools-gate__social-proof">
                Most students unlock this before their first exam.
              </p>

              <button
                className="tools-gate__dismiss"
                onClick={onClose}
              >
                Not now &mdash; I&rsquo;ll do this manually
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render inline or as portal depending on mode
  if (inline) {
    return modalContent;
  }
  return createPortal(modalContent, document.body);
};

export default UpgradeModal;
